import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AdminLayout from "../../Layout/AdminLayout";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import { getAdminSocket } from "../../Api/socket";
import AvatarImage from "../../Components/AvatarImage/AvatarImage";
import { showAlert, showConfirm } from "../../feedback/feedbackApi";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiDownload,
  FiExternalLink,
  FiRefreshCw,
  FiUserX,
  FiXCircle,
} from "react-icons/fi";
import "../Bids/Bids.scss";
import "../NewSignUps/NewSignUps.scss";
import "./PatientDetail.scss";

function getPatientDisplayName(row) {
  if (!row || typeof row !== "object") return "Unregistered";
  const raw = String(row.patientName ?? row.name ?? "").trim();
  if (!raw || raw === "-" || raw === "—" || raw === "–") return "Unregistered";
  return raw;
}

function formatDateTime(v) {
  if (!v) return "—";
  try {
    const d = typeof v === "string" ? new Date(v) : v;
    return isNaN(d.getTime()) ? "—" : d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

function displayTextOrDash(...values) {
  for (const raw of values) {
    if (raw == null) continue;
    const s = String(raw).trim();
    if (!s || s === "-" || s === "—" || s === "–") continue;
    return s;
  }
  return "—";
}

function formatDob(v) {
  if (!v) return "—";
  try {
    const d = typeof v === "string" ? new Date(v) : v;
    if (!Number.isFinite(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function formatAgeFromDob(v) {
  if (!v) return "—";
  try {
    const dob = typeof v === "string" ? new Date(v) : v;
    if (!Number.isFinite(dob.getTime())) return "—";

    const now = new Date();
    if (dob > now) return "0 days";

    let years = now.getFullYear() - dob.getFullYear();
    let months = now.getMonth() - dob.getMonth();
    let days = now.getDate() - dob.getDate();

    if (days < 0) {
      const prevMonthLastDay = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
      days += prevMonthLastDay;
      months -= 1;
    }
    if (months < 0) {
      months += 12;
      years -= 1;
    }

    if (years > 0) return `${years}y ${months}m`;
    if (months > 0) return `${months}m ${days}d`;
    return `${days}d`;
  } catch {
    return "—";
  }
}

function formatAuditMeta(meta) {
  if (meta == null || meta === "") return "—";
  if (typeof meta === "string") return meta.length > 140 ? `${meta.slice(0, 140)}…` : meta;
  try {
    const s = JSON.stringify(meta);
    return s.length > 180 ? `${s.slice(0, 180)}…` : s;
  } catch {
    return "—";
  }
}

function formatChatTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function getAdminSystemMessageText(m) {
  const p = m.payload || {};
  const type = p.type;
  const fmtINR = (n) => `Rs ${Number(n).toLocaleString("en-IN")}`;
  if (!type) return m.text || "";

  if (type === "initial_bid") return `Initial bid: ${fmtINR(p.amount)}`;
  if (type === "reverse_bid") return `Reverse bid: ${fmtINR(p.amount)}`;
  if (type === "patient_accept") return `Patient accepted: ${p.amount != null ? fmtINR(p.amount) : "offer"}.`;
  if (type === "patient_reject") return "Patient rejected the offer.";
  if (type === "patient_counter") return `Patient counter: ${fmtINR(p.amount)}`;
  if (type === "provider_accept") return "Provider accepted the patient offer.";
  if (type === "provider_reject") return "Provider rejected the offer.";

  if (type === "appointment_requested") {
    const datePart = p.date ? ` on ${new Date(p.date).toLocaleDateString()}` : "";
    const notePart = p.note ? ` — Patient note: ${p.note}` : "";
    return `Appointment requested: ${p.timeSlot || ""}${datePart}${notePart}`;
  }
  if (type === "doctor_accept_appointment") {
    return p.message
      ? `Doctor accepted. Waiting for admin approval. Message: ${p.message}`
      : "Doctor accepted. Waiting for admin approval.";
  }
  if (type === "doctor_reschedule") {
    const datePart = p.proposedDate ? ` on ${new Date(p.proposedDate).toLocaleDateString()}` : "";
    return `Doctor proposed reschedule. Waiting for admin approval: ${p.proposedTimeSlot || ""}${datePart}`;
  }
  if (type === "admin_reschedule") {
    const datePart = p.proposedDate ? ` on ${new Date(p.proposedDate).toLocaleDateString()}` : "";
    return `Admin proposed reschedule from published availability. Pending verification: ${p.proposedTimeSlot || ""}${datePart}`;
  }

  if (type === "appointment_approved") {
    const datePart = p.date ? ` on ${new Date(p.date).toLocaleDateString()}` : "";
    return `Appointment confirmed: ${p.timeSlot || ""}${datePart}`;
  }

  if (type === "treatment_bill_submitted") {
    const extra = p.needsPatientApproval ? " Patient acknowledgment requested." : "";
    return `Treatment bill: ${fmtINR(p.finalAmount)} (agreed ${fmtINR(p.agreedPrice)}), ${p.paymentMethod || "—"}.${extra}`;
  }
  if (type === "treatment_bill_patient_response") {
    return p.action === "dispute" ? "Patient disputed the treatment bill." : "Patient acknowledged the treatment bill.";
  }

  return `${type.replace(/_/g, " ")}${p.amount != null ? ` ${fmtINR(p.amount)}` : ""}`;
}

function getAdminMessageSide(m) {
  const systemType = m?.messageType === "system" ? m?.payload?.type : null;
  if (systemType === "admin_reschedule") return "admin";
  const patientSideTypes = [
    "initial_bid",
    "patient_accept",
    "patient_reject",
    "patient_counter",
    "appointment_requested",
    "treatment_bill_patient_response",
  ];
  const providerSideTypes = [
    "reverse_bid",
    "provider_accept",
    "provider_reject",
    "doctor_accept_appointment",
    "doctor_reschedule",
    "appointment_approved",
    "treatment_bill_submitted",
  ];

  if (m?.senderRole === "admin") return "admin";
  if (m?.messageType === "system" && systemType) {
    if (patientSideTypes.includes(systemType)) return "patient";
    if (providerSideTypes.includes(systemType)) return "provider";
    return "admin";
  }
  if (m?.senderRole === "user") return "patient";
  if (m?.senderRole === "doctor" || m?.senderRole === "hospital") return "provider";
  return "admin";
}

function getAdminMessageSenderLabel(m) {
  const side = getAdminMessageSide(m);
  if (side === "patient") return "Patient";
  if (side === "provider") return "Provider";
  return "Healbid Admin";
}

async function downloadPrescriptionDocument(prescriptionId, originalName, type) {
  if (!prescriptionId) return;
  try {
    const res = await API.get(endpoints.adminPrescriptionDownload(prescriptionId), { responseType: "blob" });
    const blob = res?.data;
    if (!blob || !(blob instanceof Blob)) throw new Error("No file");
    const ext =
      originalName && /\.(pdf|jpg|jpeg|png)$/i.test(originalName) ? "" : type === "report" ? "-report" : "-prescription";
    const name = (originalName && originalName.trim()) || (type === "report" ? "report" : "prescription") + ext;
    const filename = name.includes(".") ? name : name + ".pdf";
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error(err);
    showAlert(err?.response?.data?.message || "Download failed.", {
      variant: "error",
      title: "Download failed",
    });
  }
}

function isValidMongoId(id) {
  return typeof id === "string" && /^[a-f0-9]{24}$/i.test(id);
}

function docIsImagePreview(doc) {
  if (!doc?.fileUrl) return false;
  const isImage = doc.fileType && /^image\//i.test(doc.fileType);
  const isImageUrl = /\.(jpe?g|png|gif|webp)(\?|$)/i.test(doc.fileUrl);
  return !!(isImage || isImageUrl);
}

function formatActivityWhen(v) {
  if (!v) return "—";
  try {
    const d = typeof v === "string" ? new Date(v) : v;
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function ImageLightbox({ url, title, onClose }) {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    setZoom(1);
  }, [url]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="pd_lightbox_backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Image preview">
      <div className="pd_lightbox_dialog" onClick={(e) => e.stopPropagation()}>
        <div className="pd_lightbox_toolbar">
          <span className="pd_lightbox_title">{title || "Preview"}</span>
          <div className="pd_lightbox_zoom">
            <button type="button" className="pd_lightbox_icon_btn" onClick={() => setZoom((z) => Math.min(4, z + 0.25))} aria-label="Zoom in">
              +
            </button>
            <span className="pd_lightbox_zoom_pct">{Math.round(zoom * 100)}%</span>
            <button type="button" className="pd_lightbox_icon_btn" onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))} aria-label="Zoom out">
              −
            </button>
            <button type="button" className="pd_lightbox_icon_btn" onClick={() => setZoom(1)}>
              Reset
            </button>
          </div>
          <button type="button" className="pd_lightbox_close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div
          className="pd_lightbox_viewport"
          onWheel={(e) => {
            e.preventDefault();
            setZoom((z) => Math.min(4, Math.max(0.25, z + (e.deltaY > 0 ? -0.12 : 0.12))));
          }}
        >
          <img src={url} alt={title || ""} className="pd_lightbox_img" style={{ transform: `scale(${zoom})` }} draggable={false} />
        </div>
        <p className="pd_lightbox_hint">Scroll to zoom · Esc to close · Click backdrop to close</p>
      </div>
    </div>
  );
}

export default function PatientDetail() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusMatchRequestRaw = String(searchParams.get("focusMatchRequest") || "").trim();
  const focusMatchRequest = /^[a-f0-9]{24}$/i.test(focusMatchRequestRaw) ? focusMatchRequestRaw : "";

  const [patient, setPatient] = useState(null);
  const [patientLoading, setPatientLoading] = useState(true);
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescriptionsLoading, setPrescriptionsLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);

  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [conversationDetail, setConversationDetail] = useState(null);
  const [conversationLoading, setConversationLoading] = useState(false);

  const [downloadingPrescriptionId, setDownloadingPrescriptionId] = useState(null);
  const [rejectPrescriptionModal, setRejectPrescriptionModal] = useState(null);
  const [rejectPrescriptionReason, setRejectPrescriptionReason] = useState("");
  const [rejectAllPrescriptionsModal, setRejectAllPrescriptionsModal] = useState(false);
  const [rejectAllReason, setRejectAllReason] = useState("");
  const [rejectingPrescriptionId, setRejectingPrescriptionId] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [patientActionLoading, setPatientActionLoading] = useState(false);
  const [rejectPatientModal, setRejectPatientModal] = useState(false);
  const [rejectPatientReason, setRejectPatientReason] = useState("");
  const [acceptingPrescriptionId, setAcceptingPrescriptionId] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const selectedConversationIdRef = useRef(null);
  const patientIdRef = useRef(patientId);
  useEffect(() => {
    selectedConversationIdRef.current = selectedConversationId;
  }, [selectedConversationId]);
  useEffect(() => {
    patientIdRef.current = patientId;
  }, [patientId]);

  const fetchConversationDetail = useCallback(async (cid) => {
    if (!cid) return;
    setConversationLoading(true);
    setConversationDetail(null);
    try {
      const res = await API.get(endpoints.ADMIN_CONVERSATION_BY_ID(cid));
      setConversationDetail(res?.data ?? res);
    } catch {
      setConversationDetail(null);
    } finally {
      setConversationLoading(false);
    }
  }, []);

  const reloadAll = useCallback(
    async (opts = {}) => {
      const { preferFirstConversation = false, isCancelled } = opts;
      if (!isValidMongoId(patientId)) return;
      const loadForPatientId = patientId;

      setPatientLoading(true);
      setPrescriptionsLoading(true);
      setConversationsLoading(true);
      setAuditLoading(true);
      try {
        const [pRes, rxRes, convRes, auditRes] = await Promise.all([
          API.get(endpoints.adminPatientById(patientId)),
          API.get(endpoints.ADMIN_PRESCRIPTIONS, { params: { userId: patientId, limit: 100 } }),
          API.get(endpoints.ADMIN_CONVERSATIONS, { params: { patientId, limit: 50, page: 1, lastDays: 0 } }),
          API.get(endpoints.ADMIN_AUDIT_LOGS, { params: { patientId, limit: 50, page: 1 } }),
        ]);
        if (isCancelled?.()) return;
        if (String(loadForPatientId) !== String(patientIdRef.current)) return;

        const pData = pRes?.data?.data ?? pRes?.data ?? null;
        setPatient(pData && typeof pData === "object" ? pData : null);

        const docsPayload = rxRes?.data ?? {};
        const docs = docsPayload?.data ?? docsPayload ?? [];
        setPrescriptions(Array.isArray(docs) ? docs : []);

        const convPayload = convRes?.data?.data ?? convRes?.data ?? [];
        const convList = Array.isArray(convPayload) ? convPayload : [];
        setConversations(convList);

        const auditPayload = auditRes?.data?.data ?? auditRes?.data ?? {};
        const auditItems = auditPayload?.items ?? [];
        setAuditLogs(Array.isArray(auditItems) ? auditItems : []);

        const convIdStrings = (convList || []).map((c) => String(c.conversationId || c.id || c._id || ""));
        const prevSel = selectedConversationIdRef.current;
        let nextSel = null;
        if (!convList.length) {
          nextSel = null;
        } else if (preferFirstConversation) {
          const firstId = convList[0].conversationId || convList[0].id || convList[0]._id;
          nextSel = firstId ? String(firstId) : null;
        } else if (prevSel && convIdStrings.includes(String(prevSel))) {
          nextSel = String(prevSel);
        } else {
          const firstId = convList[0].conversationId || convList[0].id || convList[0]._id;
          nextSel = firstId ? String(firstId) : null;
        }
        setSelectedConversationId(nextSel);

        setLastSyncedAt(new Date());

        if (nextSel) fetchConversationDetail(nextSel);
      } catch {
        if (!isCancelled?.()) {
          setPatient(null);
          setPrescriptions([]);
          setConversations([]);
          setAuditLogs([]);
        }
      } finally {
        if (!isCancelled?.()) {
          setPatientLoading(false);
          setPrescriptionsLoading(false);
          setConversationsLoading(false);
          setAuditLoading(false);
        }
      }
    },
    [patientId, fetchConversationDetail]
  );

  useEffect(() => {
    if (!isValidMongoId(patientId)) {
      showAlert("Invalid patient link.", { variant: "error", title: "Not found" });
      navigate("/patient-list", { replace: true });
      return;
    }

    let cancelled = false;
    setSelectedConversationId(null);
    reloadAll({ preferFirstConversation: true, isCancelled: () => cancelled });

    return () => {
      cancelled = true;
    };
  }, [patientId, navigate, reloadAll]);

  useEffect(() => {
    if (!focusMatchRequest) return;
    if (patientLoading || !patient) return;
    const activity = Array.isArray(patient?.activity) ? patient.activity : [];
    if (!activity.length) {
      const next = new URLSearchParams(searchParams);
      next.delete("focusMatchRequest");
      setSearchParams(next, { replace: true });
      return;
    }
    const rowId = `pd-mr-${focusMatchRequest}`;
    const el = typeof document !== "undefined" ? document.getElementById(rowId) : null;
    const next = new URLSearchParams(searchParams);
    next.delete("focusMatchRequest");
    setSearchParams(next, { replace: true });
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("pd_activity_row--highlight");
    });
    const t = window.setTimeout(() => {
      try {
        el.classList.remove("pd_activity_row--highlight");
      } catch (_) {}
    }, 2200);
    return () => window.clearTimeout(t);
  }, [focusMatchRequest, patient, patientLoading, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isValidMongoId(patientId)) return;
    const socket = getAdminSocket();
    if (!socket) return;
    const pid = String(patientId).trim();
    const shouldReload = (payload) => {
      const p = payload?.patientId ?? payload?.meta?.patientId ?? payload?.userId;
      if (p == null) return false;
      return String(p).trim() === pid;
    };
    const onNotify = (payload) => {
      if (shouldReload(payload)) reloadAll();
    };
    const onBidUpdate = (payload) => {
      if (shouldReload(payload)) reloadAll();
    };
    socket.on("notification:new", onNotify);
    socket.on("bid:update", onBidUpdate);
    return () => {
      socket.off("notification:new", onNotify);
      socket.off("bid:update", onBidUpdate);
    };
  }, [patientId, reloadAll]);

  useEffect(() => {
    if (selectedConversationId) fetchConversationDetail(selectedConversationId);
    else {
      setConversationDetail(null);
    }
  }, [selectedConversationId, fetchConversationDetail]);

  useEffect(() => {
    if (!selectedConversationId) return;
    const socket = getAdminSocket();
    if (!socket) return;
    const cid = String(selectedConversationId).trim();
    try {
      socket.emit("conversation:join", { conversationId: cid });
    } catch (_) {}
    const onMessageNew = (payload) => {
      const convId = String(payload?.conversationId || "").trim();
      if (convId && convId === cid) fetchConversationDetail(cid);
    };
    socket.on("message:new", onMessageNew);
    return () => {
      try {
        socket.emit("conversation:leave", { conversationId: cid });
      } catch (_) {}
      socket.off("message:new", onMessageNew);
    };
  }, [selectedConversationId, fetchConversationDetail]);

  const handleUnblockPatient = useCallback(async () => {
    if (!isValidMongoId(patientId)) return;
    const ok = await showConfirm("Unblock this user? They can continue using the app.", {
      title: "Unblock user",
      confirmText: "Unblock",
    });
    if (!ok) return;
    setPatientActionLoading(true);
    try {
      await API.post(endpoints.adminPatientAccept(patientId));
      showAlert("User unblocked.", { variant: "success", title: "Unblocked" });
      await reloadAll();
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not unblock.", { variant: "error", title: "Failed" });
    } finally {
      setPatientActionLoading(false);
    }
  }, [patientId, reloadAll]);

  const submitRejectPatient = useCallback(async () => {
    const reason = String(rejectPatientReason || "").trim();
    if (!reason) {
      showAlert("Rejection reason is required.", { variant: "warning", title: "Reason required" });
      return;
    }
    if (!isValidMongoId(patientId)) return;
    setPatientActionLoading(true);
    try {
      await API.post(endpoints.adminPatientReject(patientId), { rejectionReason: reason });
      showAlert("User blocked.", { variant: "success", title: "Blocked" });
      setRejectPatientModal(false);
      setRejectPatientReason("");
      await reloadAll();
    } catch (err) {
      showAlert(err?.response?.data?.message || "Could not reject.", { variant: "error", title: "Failed" });
    } finally {
      setPatientActionLoading(false);
    }
  }, [patientId, rejectPatientReason, reloadAll]);

  const statusUi = (() => {
    const approvalStatus = String(patient?.adminApprovalStatus || "").trim().toLowerCase();
    if (approvalStatus === "rejected") return { label: "Blocked", bullet: "rejected" };
    return { label: "Active", bullet: "approved" };
  })();
  const profile = patient?.profile || {};
  const account = patient?.account || {};
  const activity = Array.isArray(patient?.activity) ? patient.activity : [];
  const dobValue =
    patient?.dob ??
    patient?.dateOfBirth ??
    patient?.birthDate ??
    patient?.date_of_birth ??
    profile?.dob ??
    profile?.dateOfBirth ??
    profile?.birthDate ??
    profile?.date_of_birth ??
    account?.dob ??
    account?.dateOfBirth ??
    account?.birthDate ??
    account?.date_of_birth;
  const bloodGroupValue = displayTextOrDash(
    patient?.bloodGroup,
    patient?.blood_group,
    patient?.bloodType,
    profile?.bloodGroup,
    profile?.blood_group,
    profile?.bloodType,
    account?.bloodGroup,
    account?.blood_group,
    account?.bloodType
  );

  const legalSummary =
    Array.isArray(account.legalConsents) && account.legalConsents.length > 0
      ? account.legalConsents
          .map((c) => `${c.document || "?"} v${c.version || "—"} (${formatDateTime(c.acceptedAt)})`)
          .join("; ")
      : "—";

  const approvalRaw = String(patient?.adminApprovalStatus || "not_submitted").toLowerCase();
  const isBlockedPatient = approvalRaw === "rejected";

  return (
    <AdminLayout>
      <div className="patient_detail_page">
        {lightbox?.url ? (
          <ImageLightbox url={lightbox.url} title={lightbox.title} onClose={() => setLightbox(null)} />
        ) : null}

        <header className="pd_header">
          <button
            type="button"
            className="patient_detail_back pd_icon_btn pd_icon_btn_header"
            onClick={() => navigate("/patient-list")}
            aria-label="Back to patient list"
            title="Back to patient list"
          >
            <FiArrowLeft className="pd_icon" aria-hidden />
          </button>
          <div className="pd_identity">
            <AvatarImage
              src={patient?.avatar || patient?.image}
              alt={getPatientDisplayName(patient || {})}
              name={getPatientDisplayName(patient || {})}
              className="pd_avatar"
              fallbackClassName="pd_avatar_fallback"
            />
            <div className="pd_identity_main">
              <h1 className="pd_title">
                {patientLoading ? "Loading…" : getPatientDisplayName(patient || {})}
              </h1>
              {!patientLoading && patient && (
                <div className="pd_chips">
                  <span className={`status_pill status_bullet status_${statusUi.bullet}`}>
                    <span className="status_dot" />
                    {statusUi.label}
                  </span>
                  <span className="pd_chip">{patient.mobile || "—"}</span>
                  <span className="pd_chip pd_chip_mono" title="Patient id">
                    {String(patient.patientId || patientId)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </header>

        {isValidMongoId(patientId) ? (
          <div className="pd_toolbar">
            <div className="pd_toolbar_left">
              {!patientLoading && patient ? (
                <>
                  {isBlockedPatient ? (
                    <button
                      type="button"
                      className="pd_btn pd_btn_primary pd_btn_icon"
                      disabled={patientActionLoading}
                      onClick={handleUnblockPatient}
                      aria-label="Unblock user"
                      title="Unblock user"
                    >
                      {patientActionLoading ? (
                        <span className="pd_icon_loading" aria-hidden>
                          …
                        </span>
                      ) : (
                        <FiCheckCircle className="pd_icon" aria-hidden />
                      )}
                    </button>
                  ) : null}
                  {!isBlockedPatient ? (
                    <button
                      type="button"
                      className="pd_btn pd_btn_danger_outline pd_btn_icon"
                      disabled={patientActionLoading}
                      onClick={() => {
                        setRejectPatientModal(true);
                        setRejectPatientReason("");
                      }}
                      aria-label="Block user"
                      title="Block user"
                    >
                      <FiUserX className="pd_icon" aria-hidden />
                    </button>
                  ) : null}
                </>
              ) : null}
            </div>
            <div className="pd_toolbar_right">
              <span className="pd_sync_hint" title="Refreshes when you use Refresh or when related events arrive">
                {lastSyncedAt ? `Synced ${formatDateTime(lastSyncedAt)}` : "Not synced yet"}
              </span>
              <button
                type="button"
                className="pd_btn pd_btn_muted pd_btn_icon"
                onClick={() => reloadAll()}
                disabled={patientLoading}
                aria-label="Refresh data"
                title="Refresh data"
              >
                <FiRefreshCw className={`pd_icon ${patientLoading ? "pd_icon_spin" : ""}`} aria-hidden />
              </button>
            </div>
          </div>
        ) : null}

        {patientLoading ? (
          <p className="patient_detail_empty">Loading patient…</p>
        ) : !patient ? (
          <p className="patient_detail_empty">Patient could not be loaded.</p>
        ) : (
          <div className="pd_body">
            <div className="pd_cols3">
              <section className="pd_card">
                <h3 className="pd_h3">Profile &amp; location</h3>
                <dl className="pd_dl">
                  <div className="pd_dl_row">
                    <dt>Phone</dt>
                    <dd>{patient.mobile || "—"}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Gender</dt>
                    <dd>{patient.gender || "—"}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>DOB</dt>
                    <dd>{formatDob(dobValue)}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Age</dt>
                    <dd>{formatAgeFromDob(dobValue)}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Blood group</dt>
                    <dd>{bloodGroupValue}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Area</dt>
                    <dd>{profile.locationLine || patient.location || "—"}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Pincode</dt>
                    <dd>{profile.locationPincode || "—"}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Coords</dt>
                    <dd>{profile.coordinates ? `${profile.coordinates.lat}, ${profile.coordinates.lng}` : "—"}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Loc. updated</dt>
                    <dd>{formatDateTime(profile.locationUpdatedAt)}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Last seen</dt>
                    <dd>{formatDateTime(profile.lastSeenAt)}</dd>
                  </div>
                  <div className="pd_dl_row pd_dl_row_span">
                    <dt>Health focus</dt>
                    <dd>{patient.healthRequestsSummary || "—"}</dd>
                  </div>
                  <div className="pd_dl_row pd_dl_row_span">
                    <dt>Note</dt>
                    <dd>{patient.optionalMessage || "—"}</dd>
                  </div>
                  {patient.rejectionReason ? (
                    <div className="pd_dl_row pd_dl_row_span">
                      <dt>Reject reason</dt>
                      <dd>{patient.rejectionReason}</dd>
                    </div>
                  ) : null}
                </dl>
              </section>

              <section className="pd_card">
                <h3 className="pd_h3">Account</h3>
                <dl className="pd_dl">
                  <div className="pd_dl_row">
                    <dt>OTP</dt>
                    <dd>{account.otpVerified ? "Yes" : "No"}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Profile</dt>
                    <dd>{account.profileComplete ? "Complete" : "Incomplete"}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Language</dt>
                    <dd>{account.preferredLanguage || "—"}</dd>
                  </div>
                  <div className="pd_dl_row pd_dl_row_span">
                    <dt>Legal</dt>
                    <dd className="pd_dd_small">{legalSummary}</dd>
                  </div>
                  <div className="pd_dl_row">
                    <dt>Bids</dt>
                    <dd>{patient.responses?.summary || "—"}</dd>
                  </div>
                </dl>
              </section>

              <section className="pd_card pd_card_stats">
                <h3 className="pd_h3">Snapshot</h3>
                <ul className="pd_stat_list">
                  <li>
                    <span className="pd_stat_label">Created</span>
                    <span className="pd_stat_val">{patient.dateCreatedFormatted || formatDateTime(patient.createdAt)}</span>
                  </li>
                  <li>
                    <span className="pd_stat_label">Threads</span>
                    <span className="pd_stat_val">{conversationsLoading ? "…" : conversations.length}</span>
                  </li>
                  <li>
                    <span className="pd_stat_label">Documents</span>
                    <span className="pd_stat_val">{prescriptionsLoading ? "…" : prescriptions.length}</span>
                  </li>
                </ul>
              </section>
            </div>

            <section className="pd_card pd_card_wide">
              <div className="pd_section_head">
                <h3 className="pd_h3">Activity</h3>
                <span className="pd_muted">{activity.length} event{activity.length === 1 ? "" : "s"}</span>
              </div>
              {activity.length === 0 ? (
                <p className="pd_muted pd_pad_sm">No requests or bids yet.</p>
              ) : (
                <div className="pd_table_wrap">
                  <table className="pd_table">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Type</th>
                        <th>Summary</th>
                        <th className="pd_th_narrow" />
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((item, idx) => (
                        <tr
                          key={`${item.kind}-${item.at}-${idx}`}
                          id={
                            item.kind === "match_request" && item.meta?.matchRequestId
                              ? `pd-mr-${String(item.meta.matchRequestId)}`
                              : undefined
                          }
                        >
                          <td className="pd_td_nowrap">{formatActivityWhen(item.at)}</td>
                          <td>
                            <span className={`pd_badge ${item.kind === "match_request" ? "pd_badge_mr" : "pd_badge_bid"}`}>
                              {item.kind === "match_request" ? "Request" : "Bid"}
                            </span>
                          </td>
                          <td>
                            <div className="pd_cell_title">{item.title}</div>
                            <div className="pd_cell_sub">{item.detail}</div>
                          </td>
                          <td className="pd_td_actions">
                            {item.meta?.conversationId ? (
                              <button
                                type="button"
                                className="pd_link_btn"
                                onClick={() => setSelectedConversationId(String(item.meta.conversationId))}
                              >
                                Chat
                              </button>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="pd_card pd_card_wide">
              <div className="pd_section_head">
                <h3 className="pd_h3">Admin audit log</h3>
                <span className="pd_muted">{auditLoading ? "…" : `${auditLogs.length} entries`}</span>
              </div>
              {auditLoading ? (
                <p className="pd_muted pd_pad_sm">Loading…</p>
              ) : auditLogs.length === 0 ? (
                <p className="pd_muted pd_pad_sm">No admin actions logged for this patient yet.</p>
              ) : (
                <div className="pd_table_wrap">
                  <table className="pd_table pd_table_compact">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Action</th>
                        <th>Actor</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((row) => (
                        <tr key={row._id}>
                          <td className="pd_td_nowrap">{formatDateTime(row.createdAt)}</td>
                          <td>
                            <code className="pd_code_sm">{row.action || "—"}</code>
                          </td>
                          <td>{row.actor?.name || row.actor?.mobile || "—"}</td>
                          <td className="pd_audit_meta">{formatAuditMeta(row.meta)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="pd_card pd_card_wide">
              <div className="pd_section_head">
                <h3 className="pd_h3">Images &amp; files</h3>
                <div className="pd_section_actions">
                  {prescriptions.some((d) => d.status !== "rejected") && (
                    <button
                      type="button"
                      className="patient_reject_all_btn pd_icon_btn pd_icon_btn_danger"
                      onClick={() => {
                        setRejectAllPrescriptionsModal(true);
                        setRejectAllReason("");
                      }}
                      aria-label="Reject all documents"
                      title="Reject all documents"
                    >
                      <FiXCircle className="pd_icon" aria-hidden />
                    </button>
                  )}
                </div>
              </div>
              {prescriptionsLoading ? (
                <p className="pd_muted pd_pad_sm">Loading…</p>
              ) : prescriptions.length === 0 ? (
                <p className="pd_muted pd_pad_sm">No documents on file.</p>
              ) : (
                <div className="pd_docs_grid">
                  {prescriptions.map((doc) => {
                    const label = doc.type === "report" ? "Report" : "Rx";
                    const title = doc.originalName || label;
                    const isImg = docIsImagePreview(doc);
                    const statusClass = doc.status === "rejected" ? "pd_tile_rejected" : "";
                    return (
                      <div key={doc._id} className={`pd_doc_tile ${statusClass}`}>
                        <button
                          type="button"
                          className="pd_doc_thumb_btn"
                          onClick={() => {
                            if (!doc.fileUrl) return;
                            if (isImg) setLightbox({ url: doc.fileUrl, title: title || label });
                            else window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
                          }}
                          aria-label={isImg ? "Zoom image" : "Open file"}
                        >
                          {isImg ? (
                            <img src={doc.fileUrl} alt={title || label} className="pd_doc_thumb" />
                          ) : (
                            <div className="pd_doc_nonimg">
                              <span className="pd_doc_nonimg_icon">{doc.fileType?.includes("pdf") ? "PDF" : "FILE"}</span>
                              <span className="pd_doc_nonimg_hint">Open</span>
                            </div>
                          )}
                        </button>
                        <div className="pd_doc_meta">
                          <div className="pd_doc_title" title={title}>
                            {label} · {doc.status || "—"}
                          </div>
                          <div className="pd_doc_name">{title}</div>
                          <div className="pd_doc_actions">
                            {doc.fileUrl && (
                              <a
                                href={doc.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pd_doc_icon_btn pd_doc_icon_link"
                                aria-label="Open in new tab"
                                title="Open in new tab"
                              >
                                <FiExternalLink className="pd_icon" aria-hidden />
                              </a>
                            )}
                            <button
                              type="button"
                              className="pd_doc_icon_btn"
                              disabled={downloadingPrescriptionId === doc._id}
                              onClick={async () => {
                                setDownloadingPrescriptionId(doc._id);
                                await downloadPrescriptionDocument(doc._id, doc.originalName, doc.type);
                                setDownloadingPrescriptionId(null);
                              }}
                              aria-label="Download"
                              title="Download"
                            >
                              {downloadingPrescriptionId === doc._id ? (
                                <span className="pd_icon_loading" aria-hidden>
                                  …
                                </span>
                              ) : (
                                <FiDownload className="pd_icon" aria-hidden />
                              )}
                            </button>
                            {doc.status === "pending" && (
                              <button
                                type="button"
                                className="pd_doc_icon_btn pd_doc_icon_accept"
                                disabled={acceptingPrescriptionId === doc._id}
                                onClick={async () => {
                                  setAcceptingPrescriptionId(doc._id);
                                  try {
                                    await API.post(endpoints.adminAcceptPrescription(doc._id));
                                    showAlert("Document accepted.", { variant: "success", title: "Accepted" });
                                    await reloadAll();
                                  } catch (err) {
                                    showAlert(err?.response?.data?.message || "Accept failed", {
                                      variant: "error",
                                      title: "Failed",
                                    });
                                  } finally {
                                    setAcceptingPrescriptionId(null);
                                  }
                                }}
                                aria-label="Accept document"
                                title="Accept document"
                              >
                                {acceptingPrescriptionId === doc._id ? (
                                  <span className="pd_icon_loading" aria-hidden>
                                    …
                                  </span>
                                ) : (
                                  <FiCheckCircle className="pd_icon" aria-hidden />
                                )}
                              </button>
                            )}
                            {doc.status !== "rejected" && (
                              <button
                                type="button"
                                className="pd_doc_icon_btn pd_doc_icon_reject"
                                disabled={rejectingPrescriptionId === doc._id}
                                onClick={() => {
                                  setRejectPrescriptionModal({ doc });
                                  setRejectPrescriptionReason("");
                                }}
                                aria-label="Reject document"
                                title="Reject document"
                              >
                                <FiXCircle className="pd_icon" aria-hidden />
                              </button>
                            )}
                          </div>
                          {doc.status === "rejected" && doc.rejectionReason && (
                            <p className="pd_doc_reason">{doc.rejectionReason}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="pd_card pd_card_wide pd_card_chat">
              <div className="pd_section_head">
                <h3 className="pd_h3">Chats</h3>
                <span className="pd_muted">Bid history</span>
              </div>
              <p className="pd_hint_inline">
                Same timeline as patient and provider: bid, reverse bid, booking, and appointment events only (no
                free-text). Refreshes live while this conversation is open.
              </p>
              {conversationsLoading ? (
                <p className="patient_detail_empty">Loading conversations…</p>
              ) : conversations.length === 0 ? (
                <p className="patient_detail_empty">No Bid History threads yet for this patient.</p>
              ) : (
                <div className="patient_detail_chat_split">
                  <div className="patient_detail_conv_list">
                    {conversations.map((c) => {
                      const id = String(c.conversationId || c.id || "");
                      const active = id === String(selectedConversationId || "");
                      const prov =
                        c.targetRole === "doctor" ? (c.providerName ? `Dr. ${c.providerName}` : "Doctor") : c.providerName || "Hospital";
                      return (
                        <button
                          key={id}
                          type="button"
                          className={`patient_detail_conv_btn ${active ? "active" : ""}`}
                          onClick={() => setSelectedConversationId(id)}
                        >
                          {prov}
                          <span className="patient_detail_conv_sub">
                            {c.lastMessagePreview ? `${c.lastMessagePreview.slice(0, 80)}` : "—"}
                          </span>
                          <span className="patient_detail_conv_sub">{formatDateTime(c.time || c.updatedAt)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="patient_detail_chat_panel">
                    {!selectedConversationId ? (
                      <p className="patient_detail_empty">Select a conversation.</p>
                    ) : conversationLoading ? (
                      <p className="patient_detail_empty">Loading chat…</p>
                    ) : !conversationDetail ? (
                      <p className="patient_detail_empty">Could not load this conversation.</p>
                    ) : (
                      <>
                        <div className="chatSummaryCard">
                          <div className="chatSummaryMain">
                            <strong>Initial bid:</strong> Rs{" "}
                            {(conversationDetail.bidHistory?.[0]?.patientBidPrice ?? 0).toLocaleString("en-IN")}
                            {conversationDetail.endAgreedPrice != null && (
                              <span className="chatSummaryAgreed">
                                <strong> Agreed:</strong> Rs {Number(conversationDetail.endAgreedPrice).toLocaleString("en-IN")}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="bidHistoryMessages bidHistoryMessagesModern">
                          {(conversationDetail.messages || []).map((m) => {
                            const side = getAdminMessageSide(m);
                            const bubbleClass =
                              side === "provider"
                                ? "admin-bhc-bubble admin-bhc-bubble-provider"
                                : side === "patient"
                                  ? "admin-bhc-bubble admin-bhc-bubble-patient"
                                  : "admin-bhc-bubble admin-bhc-bubble-admin";

                            const rowClass = side === "patient" ? "admin-bhc-row admin-bhc-row-right" : "admin-bhc-row";
                            const senderLabel = getAdminMessageSenderLabel(m);

                            const content =
                              m.messageType === "system"
                                ? getAdminSystemMessageText(m)
                                : m.senderRole === "admin"
                                  ? `Healbid Admin Says: "${m.text ?? ""}"`
                                  : m.text ?? "";

                            return (
                              <div key={m._id} className={rowClass}>
                                <div className={bubbleClass}>
                                  <div className="admin-bhc-meta">
                                    <span className="admin-bhc-sender">{senderLabel}</span>
                                    <span className="admin-bhc-timestamp">{formatChatTime(m.createdAt)}</span>
                                  </div>
                                  <div className="admin-bhc-text">{content}</div>
                                </div>
                              </div>
                            );
                          })}
                          {!(conversationDetail.messages || []).length && (
                            <div className="emptyDocState">No messages yet.</div>
                          )}
                        </div>
                        <p className="pd_muted pd_pad_sm" style={{ marginTop: 8 }}>
                          Admin cannot post custom chat text. Patient and provider actions appear here as system
                          events; use other admin tools for notices.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>

      {rejectPrescriptionModal && (
        <div className="ns_overlay ns_overlay_above" onClick={() => setRejectPrescriptionModal(null)} role="dialog" aria-modal="true">
          <div className="ns_modal ns_reject_prescription_modal" onClick={(e) => e.stopPropagation()}>
            <div className="ns_modal_head">
              <h3>Reject {rejectPrescriptionModal.doc.type === "report" ? "Report" : "Prescription"}</h3>
              <button type="button" className="ns_close" onClick={() => setRejectPrescriptionModal(null)}>
                ×
              </button>
            </div>
            <div className="ns_modal_body">
              <p className="reject_prescription_hint">Give a reason for rejection (required). The patient will see this reason.</p>
              <label className="reject_reason_label">
                <span>Reason</span>
                <textarea
                  value={rejectPrescriptionReason}
                  onChange={(e) => setRejectPrescriptionReason(e.target.value)}
                  placeholder="e.g. Document unclear, please re-upload"
                  rows={3}
                  className="reject_reason_textarea"
                />
              </label>
              <div className="doc_actions ns_modal_actions">
                <button
                  type="button"
                  className="patient_doc_download_btn"
                  disabled={
                    rejectingPrescriptionId === rejectPrescriptionModal.doc._id || !rejectPrescriptionReason.trim()
                  }
                  onClick={async () => {
                    const { doc } = rejectPrescriptionModal;
                    setRejectingPrescriptionId(doc._id);
                    try {
                      await API.post(endpoints.adminRejectPrescription(doc._id), {
                        rejectionReason: rejectPrescriptionReason.trim(),
                      });
                      setRejectPrescriptionModal(null);
                      setRejectPrescriptionReason("");
                      await reloadAll();
                    } catch (err) {
                      showAlert(err?.response?.data?.message || "Reject failed", { variant: "error", title: "Failed" });
                    } finally {
                      setRejectingPrescriptionId(null);
                    }
                  }}
                >
                  {rejectingPrescriptionId === rejectPrescriptionModal.doc._id ? "Rejecting…" : "Reject document"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rejectPatientModal && (
        <div className="ns_overlay ns_overlay_above" onClick={() => setRejectPatientModal(false)} role="dialog" aria-modal="true">
          <div className="ns_modal ns_reject_prescription_modal" onClick={(e) => e.stopPropagation()}>
            <div className="ns_modal_head">
              <h3>Block user account</h3>
              <button type="button" className="ns_close" onClick={() => setRejectPatientModal(false)}>
                ×
              </button>
            </div>
            <div className="ns_modal_body">
              <p className="reject_prescription_hint">The user will be blocked and see this reason when trying to continue.</p>
              <label className="reject_reason_label">
                <span>Reason</span>
                <textarea
                  value={rejectPatientReason}
                  onChange={(e) => setRejectPatientReason(e.target.value)}
                  placeholder="Reason for blocking"
                  rows={3}
                  className="reject_reason_textarea"
                />
              </label>
              <div className="doc_actions ns_modal_actions">
                <button
                  type="button"
                  className="patient_doc_download_btn"
                  disabled={patientActionLoading || !rejectPatientReason.trim()}
                  onClick={submitRejectPatient}
                >
                  {patientActionLoading ? "Blocking…" : "Block user"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {rejectAllPrescriptionsModal && (
        <div className="ns_overlay ns_overlay_above" onClick={() => setRejectAllPrescriptionsModal(false)} role="dialog" aria-modal="true">
          <div className="ns_modal ns_reject_prescription_modal" onClick={(e) => e.stopPropagation()}>
            <div className="ns_modal_head">
              <h3>Reject all pending documents</h3>
              <button type="button" className="ns_close" onClick={() => setRejectAllPrescriptionsModal(false)}>
                ×
              </button>
            </div>
            <div className="ns_modal_body">
              <p className="reject_prescription_hint">This will reject every non-rejected report and prescription for this patient.</p>
              <label className="reject_reason_label">
                <span>Reason</span>
                <textarea
                  value={rejectAllReason}
                  onChange={(e) => setRejectAllReason(e.target.value)}
                  placeholder="Reason shown to the patient"
                  rows={3}
                  className="reject_reason_textarea"
                />
              </label>
              <div className="doc_actions ns_modal_actions">
                <button
                  type="button"
                  className="patient_doc_download_btn"
                  disabled={!rejectAllReason.trim()}
                  onClick={async () => {
                    const pending = prescriptions.filter((d) => d.status !== "rejected").map((d) => d._id);
                    if (!pending.length) {
                      setRejectAllPrescriptionsModal(false);
                      return;
                    }
                    const reason = rejectAllReason.trim();
                    try {
                      await API.post(endpoints.ADMIN_REJECT_MULTIPLE_PRESCRIPTIONS, {
                        prescriptionIds: pending,
                        rejectionReason: reason,
                      });
                      setRejectAllPrescriptionsModal(false);
                      setRejectAllReason("");
                      await reloadAll();
                    } catch (err) {
                      showAlert(err?.response?.data?.message || "Bulk reject failed", {
                        variant: "error",
                        title: "Failed",
                      });
                    }
                  }}
                >
                  Reject all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
